/**
 * Corrispondenza fra la lingua dell'applicazione e il locale di antd.
 *
 * I testi che antd produce da sé — «Nessun dato» di `Empty`, il piè di pagina
 * della paginazione, i pulsanti di `Popconfirm`, i messaggi di validazione dei
 * form — non passano da `t()`: li sceglie il `ConfigProvider` in base al
 * proprio `locale`. Se questa tabella non seguisse la lingua attiva,
 * l'interfaccia parlerebbe due lingue nella stessa schermata.
 *
 * Solo l'italiano è importato qui. Il locale di ogni altra lingua arriva
 * **insieme alle sue risorse**, dal chunk di quella lingua, e si registra con
 * `registerAntdLocale`: così tutto ciò che riguarda una lingua sta in una
 * cartella sola, e la prima pagina non scarica i testi interni di antd in
 * lingue che nessuno leggerà.
 */

import type { Locale } from 'antd/es/locale';
import itIT from 'antd/locale/it_IT';

import { DEFAULT_LANGUAGE, type Language } from './language';

/** Locale di antd già disponibili, per lingua. */
const locales = new Map<Language, Locale>([[DEFAULT_LANGUAGE, itIT]]);

/**
 * Registra il locale di antd di una lingua.
 *
 * La chiama il caricatore delle risorse (`ensureLanguage`) prima di attivare
 * la lingua, quindi al momento del render il locale c'è già.
 *
 * @param language Lingua a cui il locale appartiene.
 * @param locale Locale di antd da usare per quella lingua.
 */
export function registerAntdLocale(language: Language, locale: Locale): void {
  locales.set(language, locale);
}

/**
 * Restituisce il locale di antd della lingua indicata.
 *
 * @param language Lingua attiva.
 * @returns Il locale da passare al `ConfigProvider`; quello italiano se la
 *   lingua non ha ancora registrato il proprio.
 */
export function antdLocaleFor(language: Language): Locale {
  return locales.get(language) ?? itIT;
}
